import { DateSegment } from './DateSegment'
import { TimeSegment } from './TimeSegment'
import { isValid } from 'date-fns'

export class DateTime {
  date: DateSegment = null
  time: TimeSegment = null

  constructor(date?: Date)
  constructor(dateSegment: DateSegment, timeSegment: TimeSegment)
  constructor(...args) {
    switch (args.length) {
      case 0:
        break // noop
      case 1:
        this.constructWithDate.apply(this, args)
        break
      case 2:
        this.constructWithSegments.apply(this, args)
        break
      default:
        throw new Error('Invalid arguments')
    }
  }

  static fromDate(date: Date | null) {
    return date && isValid(date) ? new DateTime(date) : null
  }

  static fromSegments(dateSegment: DateSegment, timeSegment: TimeSegment) {
    return new DateTime(dateSegment, timeSegment)
  }

  constructWithDate(date: Date) {
    this.constructWithSegments(new DateSegment(date), new TimeSegment(date))
  }

  constructWithSegments(dateSegment: DateSegment, timeSegment: TimeSegment) {
    this.date = dateSegment
    this.time = timeSegment
  }

  // Use NULL as a referenceDate in order to get NULL in case of incomplete DateTime (i.e. the date or time segment is missing).
  toDate(referenceDate = new Date(0)) {
    if (referenceDate === null && (!this.date || !this.time)) {
      return null
    }
    const dateSegment = this.date ?? new DateSegment(referenceDate)
    const timeSegment = this.time ?? new TimeSegment(referenceDate)
    return new Date(
      dateSegment.year,
      dateSegment.month,
      dateSegment.day,
      timeSegment.hours,
      timeSegment.minutes,
      timeSegment.seconds,
      timeSegment.milliseconds,
    )
  }

  get isComplete() {
    return Boolean(this.date) && Boolean(this.time)
  }

  /**
   * Checks whether the DateTime is partially complete, i.e. either exactly one of the segments is missing (XOR).
   * Returns false if both segments are missing or if both segments are present.
   */
  get isPartlyComplete() {
    return Boolean(this.date) !== Boolean(this.time)
  }

  get isEmpty() {
    return !Boolean(this.date) && !Boolean(this.time)
  }
}
